import type { Icon } from "@studio3t/generic-components";
import { useCallback, useMemo, useState } from "react";
import type { TestSuiteCase } from "@/types/api";
import {
  freshAssertionDraft,
  freshLocalTestId,
  freshUntitledTestId,
  isUntitledTestId,
  type StagedResource,
  type StrictMap,
  strictModeOf,
  TESTS_EDITOR_ID,
  type TestAssertionDraft,
  testRowsEqual,
  testRowsFromSuiteCase,
  untitledName,
  untitledTestResource,
  withStagedTest,
  withStrictOverride,
} from "./stagingModel";

// The Tests tab's whole state machine. Tests have no backend of their own
// yet — a suite's cases, their assertions and every draft live client-side
// here — so this module owns: the staged suite group (the panel stages ONE
// suite, interim, since the backend stores a single test_suite_id), its
// rows (`tests`), the saved assertions per row, the unsaved working copies
// per row (mirroring stagingDrafts.tsx's scriptDrafts — untitled ids are
// full rows in their own right, any number side by side), and which row
// the editor holds. The concern is split by sub-concern into the small
// hooks below, composed by useTestStaging at the bottom.
//
// pipelineStaging.tsx's useStagedLists composes useTestStaging alongside
// the source/transform/sink/schema lists; two coupling points stay
// explicit there rather than implicit here: `removeTestEverywhere`/
// `clearTests` let the lists hook fold test state into its own
// unstage-one/unstage-all operations, and `stageTest` is handed out for
// the lists hook to expose as its own staging entry point (a dropped
// test-suite explorer row stages the same way a source/transform/sink
// drop does).

// A map without one key — the row-level cleanups below all take this shape.
function dropKey<T>(map: Record<string, T>, id: string): Record<string, T> {
  const { [id]: _, ...rest } = map;
  return rest;
}

// True while the editor holds no SPECIFIC test row: nothing is selected
// (null), or the editor sits on the Tests pseudo-row (TESTS_EDITOR_ID —
// the links face with no row open). Named so setTestDraft's mint check
// reads as one intent instead of a repeated equality chain.
function editorHoldsNoTest(
  id: string | null,
): id is null | typeof TESTS_EDITOR_ID {
  return id === null || id === TESTS_EDITOR_ID;
}

// Leaving a CLEAN staged row drops its working copy (identical to the
// saved lines anyway); dirty copies and untitled drafts survive the
// switch. A pure helper (not a hook) so every editor-selection action
// below can call it without adding it to its own dependency array.
function withoutCleanCopy(
  drafts: Record<string, TestAssertionDraft[]>,
  id: string | null,
  saved: Record<string, TestAssertionDraft[]>,
): Record<string, TestAssertionDraft[]> {
  // Neither the pseudo-row nor an untitled draft has a saved counterpart
  // to diff against, so neither ever counts as "clean".
  const hasNoSavedCopy =
    id === null || id === TESTS_EDITOR_ID || isUntitledTestId(id);
  if (hasNoSavedCopy) return drafts;
  const rows = drafts[id];
  if (!rows || !testRowsEqual(rows, saved[id] ?? [])) return drafts;
  return dropKey(drafts, id);
}

// A fresh working copy of a row's saved lines to type into: a clone of
// each line (never the saved array itself, so edits can never mutate what
// was saved) when there are any, else one blank line. Shared by every path
// that hands a row's saved assertions to the editor as a NEW draft
// (opening a clean row, reverting a dirty one).
function freshCopyOfSaved(saved: TestAssertionDraft[]): TestAssertionDraft[] {
  return saved.length > 0
    ? saved.map((r) => ({ ...r }))
    : [freshAssertionDraft()];
}

// A dirty row's revert: the row on the editor keeps its live session, its
// lines snapping back to a fresh clean copy of the saved assertions; a
// background row just loses its working copy outright (nothing to show
// it, so nothing to snap back).
function discardedDraftFor(
  prev: Record<string, TestAssertionDraft[]>,
  id: string,
  testEditorId: string | null,
  testAssertions: Record<string, TestAssertionDraft[]>,
): Record<string, TestAssertionDraft[]> {
  if (testEditorId !== id) return dropKey(prev, id);
  return { ...prev, [id]: freshCopyOfSaved(testAssertions[id] ?? []) };
}

// The raw fields every sub-concern below reads or writes. Grouped in one
// place (rather than re-declared per sub-hook) so the pieces of state that
// genuinely span concerns — e.g. a suite replace touches the rows, their
// assertions AND the editor selection in one go — stay a single source of
// truth instead of drifting duplicates.
function useTestRowsState() {
  const [tests, setTests] = useState<StagedResource[]>([]);
  const [testAssertions, setTestAssertions] = useState<
    Record<string, TestAssertionDraft[]>
  >({});
  const [testDrafts, setTestDrafts] = useState<
    Record<string, TestAssertionDraft[]>
  >({});
  // Strict mode per test: the stored case's flag, and the unsaved
  // toggles (present only while they differ — see stagingModel's
  // StrictMap helpers).
  const [testStrict, setTestStrict] = useState<StrictMap>({});
  const [strictDrafts, setStrictDrafts] = useState<StrictMap>({});
  const [testEditorId, setTestEditorId] = useState<string | null>(null);
  // Whether the editor panel shows the selected test READ-ONLY (the
  // viewing face, Figma 14785-268156) instead of the assertion editor.
  const [testViewOnly, setTestViewOnly] = useState(false);
  // The staged suite group: the panel stages ONE suite (interim — the
  // backend stores one test_suite_id), its cases the `tests` rows.
  // suiteId null = PENDING (named locally, no catalog row yet).
  const [stagedSuite, setStagedSuite] = useState<{
    suiteId: string | null;
    name: string;
  } | null>(null);
  return {
    tests,
    setTests,
    testAssertions,
    setTestAssertions,
    testDrafts,
    setTestDrafts,
    testStrict,
    setTestStrict,
    strictDrafts,
    setStrictDrafts,
    testEditorId,
    setTestEditorId,
    testViewOnly,
    setTestViewOnly,
    stagedSuite,
    setStagedSuite,
  };
}

type TestRowsState = ReturnType<typeof useTestRowsState>;

// A freshly minted untitled test draft appends its staged-row face —
// drafts keep their creation order among dropped suites. Numbered by mint
// order among the open untitled rows. Its own hook since both the editor
// (typing with nothing selected) and the explicit "new test" action mint
// one, but neither needs anything else this returns.
function useAddUntitledTestRow(setTests: TestRowsState["setTests"]) {
  return useCallback(
    (id: string) => {
      setTests((prev) => [
        ...prev,
        untitledTestResource(
          id,
          untitledName(
            "Untitled test",
            prev.filter((t) => isUntitledTestId(t.id)).length,
          ),
        ),
      ]);
    },
    [setTests],
  );
}

// Minting an untitled draft (a fresh "Untitled test*" row): the two entry
// points that trigger one (typing with no test on the editor, or the
// explicit "new test" action), and the just-minted flag the row's name
// input consumes on mount.
function useTestDraftMinting(state: TestRowsState) {
  const {
    testAssertions,
    setTestDrafts,
    testEditorId,
    setTestEditorId,
    setTestViewOnly,
    setTests,
  } = state;
  const addUntitledTestRow = useAddUntitledTestRow(setTests);

  const setTestDraft = useCallback(
    (draft: TestAssertionDraft[]) => {
      // Lines arriving with no test on the editor mint an untitled draft.
      const startsUntitledDraft = editorHoldsNoTest(testEditorId);
      if (startsUntitledDraft) {
        const id = freshUntitledTestId();
        setTestDrafts((prev) => ({ ...prev, [id]: draft }));
        addUntitledTestRow(id);
        setTestEditorId(id);
        setTestViewOnly(false);
        return;
      }
      setTestDrafts((prev) => ({ ...prev, [testEditorId]: draft }));
    },
    [
      testEditorId,
      setTestDrafts,
      addUntitledTestRow,
      setTestEditorId,
      setTestViewOnly,
    ],
  );

  // The just-minted draft whose ROW should open with its name input
  // active (first-naming affordance) — consumed by the row on mount, so
  // kept drafts never reopen the input on later remounts.
  const [freshTestDraftId, setFreshTestDraftId] = useState<string | null>(null);
  const clearFreshTestDraft = useCallback(() => setFreshTestDraftId(null), []);

  // Mints a NEW untitled draft — any number can be drafted side by side;
  // the kept ones stay as their own rows (reopened via openTestInEditor).
  const openNewTestDraft = useCallback(() => {
    const id = freshUntitledTestId();
    setTestDrafts((prev) => ({
      ...withoutCleanCopy(prev, testEditorId, testAssertions),
      [id]: [freshAssertionDraft()],
    }));
    addUntitledTestRow(id);
    setTestEditorId(id);
    setTestViewOnly(false);
    setFreshTestDraftId(id);
  }, [
    testEditorId,
    testAssertions,
    setTestDrafts,
    addUntitledTestRow,
    setTestEditorId,
    setTestViewOnly,
  ]);

  return useMemo(
    () => ({
      setTestDraft,
      openNewTestDraft,
      freshTestDraftId,
      clearFreshTestDraft,
    }),
    [setTestDraft, openNewTestDraft, freshTestDraftId, clearFreshTestDraft],
  );
}

// What row the editor shows and in which face (the full assertion editor
// or the saved-lines viewer) — everything that MOVES the editor's
// selection without minting a draft.
function useTestEditorView(state: TestRowsState) {
  const {
    testAssertions,
    testDrafts,
    setTestDrafts,
    testEditorId,
    setTestEditorId,
    setTestViewOnly,
  } = state;

  // The editor's current lines — null while it shows its links face.
  const testDraft = (testEditorId !== null && testDrafts[testEditorId]) || null;

  // Loads a row into the editor: an unsaved working copy wins over the
  // saved lines; a row without either starts on one fresh line. Also
  // reopens kept untitled drafts (their ids are plain drafts keys).
  const openTestInEditor = useCallback(
    (id: string) => {
      setTestDrafts((prev) => {
        const drafts = withoutCleanCopy(prev, testEditorId, testAssertions);
        return drafts[id]
          ? drafts
          : { ...drafts, [id]: freshCopyOfSaved(testAssertions[id] ?? []) };
      });
      setTestEditorId(id);
      setTestViewOnly(false);
    },
    [
      testEditorId,
      testAssertions,
      setTestDrafts,
      setTestEditorId,
      setTestViewOnly,
    ],
  );

  // Shows a staged test's saved assertions read-only — the viewing face a
  // drop or a plain row click opens (Figma 14785-268156). No working copy
  // is minted (viewing never dirties a row); the previous row's clean copy
  // is dropped like on any editor switch.
  const openTestViewer = useCallback(
    (id: string) => {
      setTestDrafts((prev) =>
        withoutCleanCopy(prev, testEditorId, testAssertions),
      );
      setTestEditorId(id);
      setTestViewOnly(true);
    },
    [
      testEditorId,
      testAssertions,
      setTestDrafts,
      setTestEditorId,
      setTestViewOnly,
    ],
  );

  // Deselect/close: the pseudo-row takes the hand-off (editor back to its
  // links face); every draft stays kept in the pipeline.
  const closeTestEditor = useCallback(() => {
    setTestDrafts((prev) =>
      withoutCleanCopy(prev, testEditorId, testAssertions),
    );
    setTestEditorId(TESTS_EDITOR_ID);
    setTestViewOnly(false);
  }, [
    testEditorId,
    testAssertions,
    setTestDrafts,
    setTestEditorId,
    setTestViewOnly,
  ]);

  return useMemo(
    () => ({ testDraft, openTestInEditor, openTestViewer, closeTestEditor }),
    [testDraft, openTestInEditor, openTestViewer, closeTestEditor],
  );
}

// Names an untitled draft: its row becomes the staged test IN PLACE
// (keeping its position), its lines and strict flag saved, and the editor
// turns to the viewing face — a saved test reads like a dropped one. The
// icon comes from the caller — importing the Icon enum's value here would
// pull the component library into this module's (test-visible) import
// graph.
function useSaveNewTest(state: TestRowsState) {
  const {
    testDrafts,
    setTestAssertions,
    setTestDrafts,
    setTestStrict,
    strictDrafts,
    setStrictDrafts,
    setTests,
    setTestEditorId,
    setTestViewOnly,
  } = state;
  return useCallback(
    (untitledId: string, name: string, icon: Icon) => {
      const rows = testDrafts[untitledId] ?? [];
      const id = freshLocalTestId();
      setTests((prev) =>
        prev.map((t) => (t.id === untitledId ? { id, name, icon } : t)),
      );
      setTestAssertions((prev) => ({ ...prev, [id]: rows }));
      setTestDrafts((prev) => ({ ...dropKey(prev, untitledId), [id]: rows }));
      setTestStrict((prev) => ({
        ...prev,
        [id]: strictDrafts[untitledId] ?? false,
      }));
      setStrictDrafts((prev) => dropKey(prev, untitledId));
      setTestEditorId(id);
      setTestViewOnly(true);
    },
    [
      testDrafts,
      strictDrafts,
      setTests,
      setTestAssertions,
      setTestDrafts,
      setTestStrict,
      setStrictDrafts,
      setTestEditorId,
      setTestViewOnly,
    ],
  );
}

// Persists a row's working copy (lines and strict flag) onto its saved
// state — any dirty row's Save, not just the one on the editor. The row
// on the editor turns to the viewing face.
function useSaveTestEdits(state: TestRowsState) {
  const {
    testDrafts,
    setTestAssertions,
    testStrict,
    setTestStrict,
    strictDrafts,
    setStrictDrafts,
    testEditorId,
    setTestViewOnly,
  } = state;
  return useCallback(
    (id: string) => {
      const rows = testDrafts[id];
      if (!rows || isUntitledTestId(id)) return;
      setTestAssertions((prev) => ({ ...prev, [id]: rows }));
      setTestStrict((prev) => ({
        ...prev,
        [id]: strictModeOf(id, testStrict, strictDrafts),
      }));
      setStrictDrafts((prev) => dropKey(prev, id));
      if (testEditorId === id) setTestViewOnly(true);
    },
    [
      testDrafts,
      testStrict,
      strictDrafts,
      setTestAssertions,
      setTestStrict,
      setStrictDrafts,
      testEditorId,
      setTestViewOnly,
    ],
  );
}

// A row's Save actions.
function useTestSaveOps(state: TestRowsState) {
  const saveNewTest = useSaveNewTest(state);
  const saveTestEdits = useSaveTestEdits(state);
  return useMemo(
    () => ({ saveNewTest, saveTestEdits }),
    [saveNewTest, saveTestEdits],
  );
}

// A row's revert/rename/dirty bookkeeping — the read side (isTestDirty)
// and the two actions that don't touch the saved assertions at all.
// The strict flag the editor shows and toggles for a row (stagingModel's
// StrictMap rules: an override exists only while it differs from saved).
function useStrictModeOps(state: TestRowsState) {
  const { testStrict, strictDrafts, setStrictDrafts } = state;
  const strictModeFor = useCallback(
    (id: string) => strictModeOf(id, testStrict, strictDrafts),
    [testStrict, strictDrafts],
  );
  const setStrictMode = useCallback(
    (id: string, value: boolean) => {
      setStrictDrafts((prev) =>
        withStrictOverride(prev, id, value, testStrict),
      );
    },
    [testStrict, setStrictDrafts],
  );
  return useMemo(
    () => ({ strictModeOf: strictModeFor, setStrictMode }),
    [strictModeFor, setStrictMode],
  );
}

function useTestRevertOps(state: TestRowsState) {
  const {
    testAssertions,
    testDrafts,
    setTestDrafts,
    strictDrafts,
    setStrictDrafts,
    testEditorId,
    setTests,
  } = state;
  const strictOps = useStrictModeOps(state);

  // Returns a dirty row to its saved lines (the revert row action, any
  // dirty row's — not just the open one).
  const discardTestEdits = useCallback(
    (id: string) => {
      if (isUntitledTestId(id)) return;
      setTestDrafts((prev) =>
        discardedDraftFor(prev, id, testEditorId, testAssertions),
      );
      setStrictDrafts((prev) => dropKey(prev, id));
    },
    [testEditorId, testAssertions, setTestDrafts, setStrictDrafts],
  );

  // Client-side rename — the local id stays, so the editor, drafts and
  // saved assertions follow along untouched.
  const renameTest = useCallback(
    (id: string, name: string) => {
      setTests((prev) => prev.map((t) => (t.id === id ? { ...t, name } : t)));
    },
    [setTests],
  );

  const isTestDirty = useCallback(
    (id: string) => {
      if (id in strictDrafts) return true;
      const rows = testDrafts[id];
      return rows != null && !testRowsEqual(rows, testAssertions[id] ?? []);
    },
    [testDrafts, testAssertions, strictDrafts],
  );

  return useMemo(
    () => ({ discardTestEdits, renameTest, isTestDirty, ...strictOps }),
    [discardTestEdits, renameTest, isTestDirty, strictOps],
  );
}

// Whether a row exists at all: staging one from the explorer, discarding
// an untitled draft outright, or unstaging a row from anywhere it's
// referenced (the tests-tab counterpart to the other sections' unstage).
function useTestRowMembership(state: TestRowsState) {
  const {
    setTests,
    setTestAssertions,
    setTestDrafts,
    setTestStrict,
    setStrictDrafts,
    testEditorId,
    setTestEditorId,
    setTestViewOnly,
  } = state;

  const stageTest = useCallback(
    (resource: StagedResource) => {
      setTests((prev) => withStagedTest(prev, resource));
    },
    [setTests],
  );

  // An untitled row's minus — the one path that throws its draft away
  // (the row goes with it).
  const discardUntitledTest = useCallback(
    (untitledId: string) => {
      setTests((prev) => prev.filter((t) => t.id !== untitledId));
      setTestDrafts((prev) => dropKey(prev, untitledId));
      setStrictDrafts((prev) => dropKey(prev, untitledId));
      setTestEditorId((prev) => (prev === untitledId ? TESTS_EDITOR_ID : prev));
    },
    [setTests, setTestDrafts, setStrictDrafts, setTestEditorId],
  );

  // An unstaged test takes its saved assertions and working copy along;
  // if it was the one on the editor, the tests pseudo-row takes the
  // hand-off (editor back to its links face, pseudo-row highlighted) —
  // the same move the stage tabs make to their editor rows.
  const removeTestEverywhere = useCallback(
    (id: string) => {
      setTests((prev) =>
        prev.some((t) => t.id === id) ? prev.filter((t) => t.id !== id) : prev,
      );
      setTestAssertions((prev) => dropKey(prev, id));
      setTestDrafts((prev) => dropKey(prev, id));
      setTestStrict((prev) => dropKey(prev, id));
      setStrictDrafts((prev) => dropKey(prev, id));
      if (testEditorId === id) {
        setTestEditorId(TESTS_EDITOR_ID);
        setTestViewOnly(false);
      }
    },
    [
      testEditorId,
      setTests,
      setTestAssertions,
      setTestDrafts,
      setTestStrict,
      setStrictDrafts,
      setTestEditorId,
      setTestViewOnly,
    ],
  );

  return useMemo(
    () => ({ stageTest, discardUntitledTest, removeTestEverywhere }),
    [stageTest, discardUntitledTest, removeTestEverywhere],
  );
}

// A dropped suite's cases as staged rows: fresh local ids (the backend
// case has none of its own yet in this panel) paired with the assertion
// rows parsed out of each case.
function stagedRowsFromSuite(
  suite: { id: string; name: string; cases: TestSuiteCase[] },
  icon: Icon,
): Array<{
  resource: StagedResource;
  rows: TestAssertionDraft[];
  strictMode: boolean;
}> {
  return suite.cases.map((c) => ({
    resource: { id: freshLocalTestId(), name: c.name, icon },
    rows: testRowsFromSuiteCase(c),
    strictMode: c.strict_mode ?? false,
  }));
}

// Replacing or clearing the WHOLE staged group: stage a dropped suite,
// stage a pending (uncataloged) one, or unstage back to empty. All three
// rewrite the rows, assertions and editor selection atomically — nothing
// here is left half-applied for the row-level hooks to reconcile
// afterwards — so they share one reset for the common "back to empty"
// tail (stagePendingSuite and unstageSuiteGroup differ from each other
// only in what the header becomes).
function useSuiteReplace(state: TestRowsState) {
  const {
    setTests,
    setTestAssertions,
    setTestDrafts,
    setTestStrict,
    setStrictDrafts,
    setTestEditorId,
    setTestViewOnly,
    setStagedSuite,
  } = state;

  const resetGroupRows = useCallback(() => {
    setTests([]);
    setTestAssertions({});
    setTestDrafts({});
    setTestStrict({});
    setStrictDrafts({});
    setTestEditorId(TESTS_EDITOR_ID);
    setTestViewOnly(false);
  }, [
    setTests,
    setTestAssertions,
    setTestDrafts,
    setTestStrict,
    setStrictDrafts,
    setTestEditorId,
    setTestViewOnly,
  ]);

  // REPLACES the staged group: the suite's cases become the panel's rows
  // (name-keyed local copies like a drop always staged), every draft
  // goes — callers confirm-gate on hasDirtyTestWork first. The group
  // comes up with its first test open in the viewer (a dropped or
  // restored suite lands ready to read); an empty suite has nothing to
  // view, so the group itself takes the selection (its links face offers
  // Add / Generate).
  const stageSuiteGroup = useCallback(
    (
      suite: { id: string; name: string; cases: TestSuiteCase[] },
      icon: Icon,
    ) => {
      const staged = stagedRowsFromSuite(suite, icon);
      setStagedSuite({ suiteId: suite.id, name: suite.name });
      setTests(staged.map((s) => s.resource));
      setTestAssertions(
        Object.fromEntries(staged.map((s) => [s.resource.id, s.rows])),
      );
      setTestDrafts({});
      setTestStrict(
        Object.fromEntries(staged.map((s) => [s.resource.id, s.strictMode])),
      );
      setStrictDrafts({});
      const first = staged[0]?.resource.id ?? TESTS_EDITOR_ID;
      setTestEditorId(first);
      setTestViewOnly(first !== TESTS_EDITOR_ID);
    },
    [
      setStagedSuite,
      setTests,
      setTestAssertions,
      setTestDrafts,
      setTestStrict,
      setStrictDrafts,
      setTestEditorId,
      setTestViewOnly,
    ],
  );

  // "Add a new test suite" stages a PENDING group: nothing is written to
  // the catalog (no tree row) until the suite holds its first non-empty
  // test — the first case write creates the stored suite and attaches
  // its id (useEnsureStagedSuite).
  const stagePendingSuite = useCallback(
    (name: string) => {
      setStagedSuite({ suiteId: null, name });
      resetGroupRows();
    },
    [setStagedSuite, resetGroupRows],
  );

  const unstageSuiteGroup = useCallback(() => {
    setStagedSuite(null);
    resetGroupRows();
  }, [setStagedSuite, resetGroupRows]);

  return useMemo(
    () => ({ stageSuiteGroup, stagePendingSuite, unstageSuiteGroup }),
    [stageSuiteGroup, stagePendingSuite, unstageSuiteGroup],
  );
}

// Appends already-persisted cases as saved rows after the staged ones
// (test generation lands its batch this way); drafts and the selection
// stay untouched. Returns the minted ids, so a caller can open what it
// just added.
function useAppendSavedTests(state: TestRowsState) {
  const { setTests, setTestAssertions, setTestStrict } = state;
  return useCallback(
    (
      entries: Array<{
        name: string;
        rows: TestAssertionDraft[];
        strictMode?: boolean;
      }>,
      icon: Icon,
    ) => {
      const staged = entries.map((entry) => ({
        resource: { id: freshLocalTestId(), name: entry.name, icon },
        rows: entry.rows,
        strictMode: entry.strictMode ?? false,
      }));
      setTests((prev) => [...prev, ...staged.map((s) => s.resource)]);
      setTestAssertions((prev) => ({
        ...prev,
        ...Object.fromEntries(staged.map((s) => [s.resource.id, s.rows])),
      }));
      setTestStrict((prev) => ({
        ...prev,
        ...Object.fromEntries(staged.map((s) => [s.resource.id, s.strictMode])),
      }));
      return staged.map((s) => s.resource.id);
    },
    [setTests, setTestAssertions, setTestStrict],
  );
}

// The staged suite header's own fields (catalog id, name) once a group
// already exists, plus the replace/unstage confirm gate — everything
// about the group that ISN'T a full rewrite of its rows.
function useSuiteMeta(state: TestRowsState) {
  const {
    tests,
    testAssertions,
    testDrafts,
    strictDrafts,
    stagedSuite,
    setStagedSuite,
  } = state;

  const attachSuiteId = useCallback(
    (suiteId: string) => {
      setStagedSuite((prev) => (prev ? { ...prev, suiteId } : prev));
    },
    [setStagedSuite],
  );

  const appendSavedTests = useAppendSavedTests(state);

  const renameStagedSuite = useCallback(
    (name: string) => {
      setStagedSuite((prev) => (prev ? { ...prev, name } : prev));
    },
    [setStagedSuite],
  );

  // The replace/unstage confirm gate: an untitled draft or any dirty row.
  const hasDirtyTestWork = useCallback(
    () =>
      tests.some((t) => isUntitledTestId(t.id)) ||
      tests.some((t) => {
        if (t.id in strictDrafts) return true;
        const rows = testDrafts[t.id];
        return rows != null && !testRowsEqual(rows, testAssertions[t.id] ?? []);
      }),
    [tests, testDrafts, testAssertions, strictDrafts],
  );

  return useMemo(
    () => ({
      stagedSuite,
      attachSuiteId,
      appendSavedTests,
      renameStagedSuite,
      hasDirtyTestWork,
    }),
    [
      stagedSuite,
      attachSuiteId,
      appendSavedTests,
      renameStagedSuite,
      hasDirtyTestWork,
    ],
  );
}

export function useTestStaging() {
  const state = useTestRowsState();
  const draftMinting = useTestDraftMinting(state);
  const editorView = useTestEditorView(state);
  const saveOps = useTestSaveOps(state);
  const revertOps = useTestRevertOps(state);
  const rowMembership = useTestRowMembership(state);
  const suiteReplace = useSuiteReplace(state);
  const suiteMeta = useSuiteMeta(state);

  // The full reset ("Clear and start fresh" / replacing the group):
  // every row, assertion, draft and the suite header go together — the
  // one operation that spans every sub-concern above at once.
  const clearTests = useCallback(() => {
    state.setTests([]);
    state.setTestAssertions({});
    state.setTestDrafts({});
    state.setTestStrict({});
    state.setStrictDrafts({});
    state.setTestEditorId(null);
    state.setTestViewOnly(false);
    state.setStagedSuite(null);
  }, [
    state.setTests,
    state.setTestAssertions,
    state.setTestDrafts,
    state.setTestStrict,
    state.setStrictDrafts,
    state.setTestEditorId,
    state.setTestViewOnly,
    state.setStagedSuite,
  ]);

  return useMemo(
    () => ({
      tests: state.tests,
      testAssertions: state.testAssertions,
      testDrafts: state.testDrafts,
      testEditorId: state.testEditorId,
      testViewOnly: state.testViewOnly,
      ...draftMinting,
      ...editorView,
      ...saveOps,
      ...revertOps,
      ...rowMembership,
      ...suiteReplace,
      ...suiteMeta,
      clearTests,
    }),
    [
      state.tests,
      state.testAssertions,
      state.testDrafts,
      state.testEditorId,
      state.testViewOnly,
      draftMinting,
      editorView,
      saveOps,
      revertOps,
      rowMembership,
      suiteReplace,
      suiteMeta,
      clearTests,
    ],
  );
}

/** The test-staging hook's shape — the piece useStagedLists composes on. */
export type TestStaging = ReturnType<typeof useTestStaging>;
